// eslint-disable-next-line import/no-extraneous-dependencies
import * as uuid from 'uuid';
import { GetAuthResponseTgt } from './response';

enum AuthService {
  KROGER = 'kro',
}

enum AuthStatus {
  WAITING = 'waiting',
  SUCCESS = 'success',
  ERROR = 'error',
}

type AuthReferenceId = string;

type Token = {
  refreshToken: string;
  expiresIn: number;
  accessToken: string;
  tokenType: string;
  valid: boolean;
}

type AuthResponse = {
  status: AuthStatus;
  refreshToken: string;
  expiresIn: number;
  accessToken: string;
  tokenType: string;
  valid: boolean;
}

type AuthReference = {
  uri: string;
  referenceId: AuthReferenceId
}

type AuthOnBehalfOfConfig = {
  baseUri: string;
  clientId: string;
  scope: string;
  responseType: string;
  redirectUri: string;
}

const getReferenceId = (): AuthReferenceId => uuid.v4();

const getCurrentAccessToken = (authService: AuthService): Token => {
    const key = `auth-${authService}`;
    const lsToken = localStorage.getItem(key);
    if (!lsToken) {
        return {
            refreshToken: '',
            expiresIn: 0,
            accessToken: '',
            tokenType: '',
            valid: false,
        };
    }
    return JSON.parse(lsToken) as Token;
};

const clearToken = (authService: AuthService) => {
    const key = `auth-${authService}`;
    localStorage.removeItem(key);
};

const handleResponse = async (authService: AuthService, response: Response): Promise<AuthResponse> => {
    if (response.ok) {
        const key = `auth-${authService}`;
        const data = await response.json();
        const resp = {
            status: AuthStatus.SUCCESS,
            ...data,
        };
        localStorage.setItem(key, JSON.stringify(resp));
        return resp;
    }
    if (response.status === 404) {
        return {
            status: AuthStatus.WAITING,
            refreshToken: '',
            expiresIn: 0,
            accessToken: '',
            tokenType: '',
            valid: false,
        };
    }
    return {
        status: AuthStatus.ERROR,
        refreshToken: '',
        expiresIn: 0,
        accessToken: '',
        tokenType: '',
        valid: false,
    };
};

const apiUrl = 'https://products.polaris.me/v1/integrations';

const fetchAccessToken = async (authService: AuthService, referenceId: string): Promise<AuthResponse> => {
    try {
        const request = new Request(`${apiUrl}/${authService}/auth/token?id=${referenceId}`);
        const response = await fetch(request);
        return handleResponse(authService, response);
    } catch (err) {
        console.error('[MERCURY AUTH] ', err);
        return {
            status: AuthStatus.ERROR,
            refreshToken: '',
            expiresIn: 0,
            accessToken: '',
            tokenType: '',
            valid: false,
        };
    }
};

const refreshAccessToken = async (authService: AuthService, token: Token): Promise<AuthResponse> => {
    try {
        const request = new Request(`${apiUrl}/integrations/${authService}/auth/refresh?token=${token.refreshToken}`);
        const response = await fetch(request);
        return handleResponse(authService, response);
    } catch (err) {
        console.error('[MERCURY AUTH] ', err);
        return {
            status: AuthStatus.ERROR,
            refreshToken: '',
            expiresIn: 0,
            accessToken: '',
            tokenType: '',
            valid: false,
        };
    }
};

class TgtItems {
    public quantity: Number;

    public upc: String;

    constructor(
        quantity: Number,
        upc: string,
    ) {
        this.quantity = quantity;
        this.upc = upc;
    }
}

const getAuthTgt = async (items: TgtItems[], locationId: string): Promise<[GetAuthResponseTgt, Error | null]> => {
    const endpoint = `${apiUrl}/tgt/cart`;
    const payload = {
        locationId,
        items,
    };
    try {
        const headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };
        const fetchOpts = {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        };
        const response = await fetch(endpoint, fetchOpts);
        const data = await response.json() as GetAuthResponseTgt;
        return [data, null];
    } catch (err) {
        return [{ accessToken: '' } as GetAuthResponseTgt, err as Error];
    }
};

export {
    AuthReferenceId,
    AuthReference,
    Token,
    AuthStatus,
    AuthService,
    AuthResponse,
    AuthOnBehalfOfConfig,
    getReferenceId,
    getCurrentAccessToken,
    fetchAccessToken,
    refreshAccessToken,
    clearToken,
    TgtItems,
    getAuthTgt,
};
